/**
 * Subtitles service supporting multiple providers:
 * - OpenSubtitles (via Stremio addon) - No API key required
 * - Subdl - API key required, better release info
 */

const OPENSUBTITLES_ADDON = "https://opensubtitles-v3.strem.io";
const SUBDL_API = "https://api.subdl.com/api/v1/subtitles";

// Subdl API key (optional, can be set in localStorage)
const getSubdlApiKey = () => {
  return localStorage.getItem("subdl_api_key") || import.meta.env.VITE_SUBDL_API_KEY || "";
};

/**
 * Get subtitles from OpenSubtitles (Stremio addon)
 */
export const getOpenSubtitles = async (imdbId) => {
  if (!imdbId) return [];

  try {
    const response = await fetch(`${OPENSUBTITLES_ADDON}/subtitles/movie/${imdbId}.json`);
    if (!response.ok) return [];

    const data = await response.json();
    if (!data.subtitles || data.subtitles.length === 0) return [];

    return data.subtitles.map((sub) => ({
      id: sub.id,
      lang: sub.lang,
      langName: getLanguageName(sub.lang),
      url: sub.url,
      release: `OpenSub #${sub.id}`,
      source: "opensubtitles",
    }));
  } catch (error) {
    console.error("Error fetching OpenSubtitles:", error);
    return [];
  }
};

/**
 * Get subtitles from Subdl
 */
export const getSubdlSubtitles = async (imdbId, languages = "ES,EN") => {
  const apiKey = getSubdlApiKey();
  if (!apiKey) {
    console.error("Subdl API key not configured");
    return [];
  }
  if (!imdbId) return [];

  try {
    const params = new URLSearchParams({
      api_key: apiKey,
      imdb_id: imdbId,
      languages: languages,
      subs_per_page: "30",
      releases: "1",
    });

    const response = await fetch(`${SUBDL_API}?${params}`);
    if (!response.ok) return [];

    const data = await response.json();
    if (!data.status || !data.subtitles) return [];

    return data.subtitles.map((sub) => ({
      id: sub.sd_id || sub.subdl_link,
      lang: sub.lang,
      langName: getLanguageName(sub.lang?.toLowerCase()),
      url: `https://dl.subdl.com${sub.url}`,
      release: sub.release_name || sub.name || "Unknown",
      author: sub.author?.name || "",
      source: "subdl",
      isZip: true,
    }));
  } catch (error) {
    console.error("Error fetching Subdl:", error);
    return [];
  }
};

/**
 * Get subtitles from the selected source
 */
export const getSubtitles = async (imdbId, source = "opensubtitles") => {
  if (source === "subdl") {
    return await getSubdlSubtitles(imdbId);
  }
  return await getOpenSubtitles(imdbId);
};

/**
 * Check if Subdl is available (API key configured)
 */
export const isSubdlAvailable = () => {
  return !!getSubdlApiKey();
};

/**
 * Save Subdl API key
 */
export const setSubdlApiKey = (apiKey) => {
  localStorage.setItem("subdl_api_key", apiKey);
};

/**
 * Get language name from ISO code
 */
const getLanguageName = (code) => {
  const languages = {
    spa: "Español",
    es: "Español",
    spanish: "Español",
    eng: "English",
    en: "English",
    english: "English",
    por: "Português",
    pt: "Português",
    fre: "Français",
    fr: "Français",
    ger: "Deutsch",
    de: "Deutsch",
    ita: "Italiano",
    it: "Italiano",
    dut: "Nederlands",
    nl: "Nederlands",
    pol: "Polski",
    pl: "Polski",
    rus: "Русский",
    ru: "Русский",
    tur: "Türkçe",
    tr: "Türkçe",
    ara: "العربية",
    ar: "العربية",
    heb: "עברית",
    he: "עברית",
    jpn: "日本語",
    ja: "日本語",
    kor: "한국어",
    ko: "한국어",
    chi: "中文",
    zh: "中文",
    hin: "हिन्दी",
    hi: "हिन्दी",
    gre: "Ελληνικά",
    el: "Ελληνικά",
    cze: "Čeština",
    cs: "Čeština",
    hun: "Magyar",
    hu: "Magyar",
    ron: "Română",
    ro: "Română",
    swe: "Svenska",
    sv: "Svenska",
    nor: "Norsk",
    no: "Norsk",
    dan: "Dansk",
    da: "Dansk",
    fin: "Suomi",
    fi: "Suomi",
  };
  return languages[code] || (code || "").toUpperCase();
};

/**
 * Filter subtitles by preferred languages
 */
export const filterSubtitlesByLanguage = (subtitles, preferredLangs = ["spa", "eng", "es", "en"]) => {
  const normalizedPreferred = preferredLangs.map((l) => l.toLowerCase());
  const preferred = subtitles.filter((sub) => normalizedPreferred.some((lang) => sub.lang?.toLowerCase() === lang || sub.langName?.toLowerCase().includes(lang)));
  const other = subtitles.filter((sub) => !normalizedPreferred.some((lang) => sub.lang?.toLowerCase() === lang || sub.langName?.toLowerCase().includes(lang)));
  return [...preferred, ...other];
};
